// ─── Sprout Quest — Pixel Art HTML Theme (bridge protocol v3) ────────────────
// Visual style: Sprout Lands / Stardew Valley / Zelda-cozy
// Warm cream panels · chunky brown borders · 3D pressed buttons · grass BG
// Hearts for progress · coins for score · parchment result scroll

export const PIXEL_ART_HTML = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1,maximum-scale=1">
<meta name="capsule-theme-name" content="Sprout Quest">
<meta name="capsule-theme-version" content="2.0.0">
<meta name="capsule-theme-style" content="pixel-art,cozy,rpg,sprout-lands">
<title>Sprout Quest</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Press+Start+2P&display=swap" rel="stylesheet">
<style>
/* ─── Palette ─── */
:root{
  --bg:#8ab834;
  --panel:#ead9a8; --panel-inner:#c4a870; --panel-dark:#d4c088;
  --border:#5c3d1e; --shadow:#3d2810;
  --text:#3d2810; --text-dim:#7a5c30; --text-light:#fef0c0;
  --red:#d04040;  --red-bg:#f0b0a0; --red-border:#802020; --red-shadow:#601010;
  --green:#5aaa30; --green-bg:#c0e890; --green-border:#3d7018; --green-shadow:#2a5010;
  --gold:#d89a00;  --gold-bg:#fde880; --gold-border:#a07000; --gold-shadow:#705000;
  --blue:#4090c0;  --blue-bg:#b8ddf0; --blue-border:#2060a0; --blue-shadow:#104080;
  --font:'Press Start 2P',monospace;
}

/* ─── Reset ─── */
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html,body{height:100%;font-family:var(--font);font-size:8px;color:var(--text);}

/* ─── Grass background (pixel tile) ─── */
body{
  background-color:var(--bg);
  background-image:
    repeating-linear-gradient(transparent 0,transparent 7px,rgba(0,0,0,.045) 7px,rgba(0,0,0,.045) 8px),
    repeating-linear-gradient(90deg,transparent 0,transparent 7px,rgba(0,0,0,.045) 7px,rgba(0,0,0,.045) 8px);
  overflow:hidden;
}

/* ─── HUD (top bar — parchment) ─── */
#hud{
  position:fixed;top:0;left:0;right:0;z-index:100;
  background:var(--panel);
  border-bottom:4px solid var(--border);
  box-shadow:0 6px 0 0 var(--shadow);
  padding:7px 12px;
  display:flex;align-items:center;gap:10px;flex-wrap:wrap;
}
#hud-title{flex:1;font-size:7px;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
#hud-hearts{display:flex;gap:3px;align-items:center;flex-shrink:0;}
.hrt{font-size:13px;line-height:1;}
.hrt-on{color:var(--red);}
.hrt-off{color:rgba(160,80,80,.3);}
#hud-coins{font-size:7px;color:var(--gold-border);white-space:nowrap;flex-shrink:0;}

/* ─── Slide area ─── */
#game{position:fixed;top:48px;bottom:56px;left:0;right:0;overflow-y:auto;padding:14px 12px;z-index:1;}
#slide-wrap{max-width:560px;margin:0 auto;display:flex;flex-direction:column;gap:12px;}

/* ─── Footer ─── */
#footer{
  position:fixed;bottom:0;left:0;right:0;z-index:100;
  background:var(--panel);
  border-top:4px solid var(--border);
  padding:8px 12px;
  display:flex;align-items:center;justify-content:center;gap:12px;
}

/* ─── Parchment panel (content) ─── */
.px-panel{
  background:var(--panel);
  border:4px solid var(--border);
  box-shadow:0 6px 0 0 var(--shadow),inset 0 0 0 2px var(--panel-inner);
  padding:14px 14px;
}
.px-panel-blue{
  background:#dceef8;
  border:4px solid var(--blue-border);
  box-shadow:0 6px 0 0 var(--blue-shadow),inset 0 0 0 2px #90c8e8;
  padding:14px;
}
.px-panel-gold{
  background:#fef8d0;
  border:4px solid var(--gold-border);
  box-shadow:0 6px 0 0 var(--gold-shadow),inset 0 0 0 2px #e8c860;
  padding:14px;
}

/* ─── Slide typography ─── */
.slide-type{font-size:6px;color:var(--text-dim);letter-spacing:.12em;text-transform:uppercase;margin-bottom:8px;}
.slide-title{font-size:10px;color:var(--text);line-height:1.7;margin-bottom:10px;}
.slide-body{font-size:7px;color:var(--text-dim);line-height:2.2;}
.slide-img{width:100%;max-height:240px;object-fit:cover;image-rendering:pixelated;border:4px solid var(--border);box-shadow:0 5px 0 0 var(--shadow);display:block;}

/* ─── RPG Dialog box (question) ─── */
.dialog{
  background:var(--panel);
  border:5px solid var(--border);
  box-shadow:0 7px 0 0 var(--shadow),inset 0 0 0 3px var(--panel-inner);
  padding:14px 14px 22px;
  position:relative;
}
.dialog-text{font-size:8px;color:var(--text);line-height:2.1;}
.dialog-arrow{position:absolute;bottom:6px;right:10px;font-size:8px;color:var(--border);animation:blink 1s step-end infinite;}

/* ─── Primary action button (green) ─── */
.px-btn{
  font-family:var(--font);font-size:8px;
  background:var(--green);color:var(--text-light);
  border:4px solid var(--green-border);
  box-shadow:0 6px 0 0 var(--green-shadow);
  padding:10px 18px;cursor:pointer;
  position:relative;top:0;
  line-height:1.5;letter-spacing:.02em;
  transition:top .06s,box-shadow .06s,background .06s;
  display:inline-flex;align-items:center;gap:8px;justify-content:center;
}
.px-btn:hover:not(:disabled){background:#7ac450;}
.px-btn:active:not(:disabled){top:4px;box-shadow:0 2px 0 0 var(--green-shadow);}
.px-btn:disabled{opacity:.45;cursor:not-allowed;}
.px-btn-wide{width:100%;max-width:320px;}

/* Secondary button (brown/parchment) */
.px-btn-sec{
  background:var(--panel-dark);color:var(--text);
  border:4px solid var(--border);
  box-shadow:0 6px 0 0 var(--shadow);
}
.px-btn-sec:hover:not(:disabled){background:#e4d090;}
.px-btn-sec:active:not(:disabled){top:4px;box-shadow:0 2px 0 0 var(--shadow);}

/* Gold button */
.px-btn-gold{
  background:var(--gold-bg);color:var(--text);
  border:4px solid var(--gold-border);
  box-shadow:0 6px 0 0 var(--gold-shadow);
}
.px-btn-gold:hover:not(:disabled){background:#ffe898;}
.px-btn-gold:active:not(:disabled){top:4px;box-shadow:0 2px 0 0 var(--gold-shadow);}

/* ─── Option buttons ─── */
.options{display:flex;flex-direction:column;gap:8px;}
.opt-btn{
  font-family:var(--font);font-size:7px;
  background:var(--panel);color:var(--text);
  border:4px solid var(--border);
  box-shadow:0 5px 0 0 var(--shadow);
  padding:10px 12px;cursor:pointer;
  position:relative;top:0;
  text-align:left;
  display:flex;align-items:center;gap:10px;
  line-height:1.8;width:100%;
  transition:top .06s,box-shadow .06s,background .06s;
}
.opt-btn:hover:not(:disabled){background:#f8e8c0;}
.opt-btn.selected,.opt-btn:active:not(:disabled){top:3px;box-shadow:0 2px 0 0 var(--shadow);background:#f0e0b0;}
.opt-btn.correct{background:var(--green-bg);border-color:var(--green-border);box-shadow:0 5px 0 0 var(--green-shadow);color:#1a4010;top:0;}
.opt-btn.wrong{background:var(--red-bg);border-color:var(--red-border);box-shadow:0 5px 0 0 var(--red-shadow);color:#601010;top:0;}
.opt-btn:disabled{cursor:default;}

/* Option key badge */
.opt-key{
  width:24px;height:24px;flex-shrink:0;
  background:var(--panel-dark);
  border:3px solid var(--border);
  box-shadow:0 3px 0 0 var(--shadow);
  display:flex;align-items:center;justify-content:center;
  font-size:6px;color:var(--text);
  transition:background .06s,border-color .06s;
}
.opt-btn.correct .opt-key{background:var(--green);border-color:var(--green-border);box-shadow:0 3px 0 0 var(--green-shadow);color:var(--text-light);}
.opt-btn.wrong .opt-key{background:var(--red);border-color:var(--red-border);box-shadow:0 3px 0 0 var(--red-shadow);color:var(--text-light);}

/* ─── Image options ─── */
.img-opts{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
.img-opt{
  font-family:var(--font);font-size:6px;
  background:var(--panel);color:var(--text);
  border:4px solid var(--border);
  box-shadow:0 5px 0 0 var(--shadow);
  padding:8px;cursor:pointer;
  position:relative;top:0;
  display:flex;flex-direction:column;align-items:center;gap:6px;
  text-align:center;line-height:1.7;
  transition:top .06s,box-shadow .06s;
}
.img-opt:hover:not(:disabled){background:#f8e8c0;}
.img-opt:active:not(:disabled){top:3px;box-shadow:0 2px 0 0 var(--shadow);}
.img-opt img{width:100%;max-height:80px;object-fit:cover;image-rendering:pixelated;border:3px solid var(--border);}
.img-opt.correct{background:var(--green-bg);border-color:var(--green-border);box-shadow:0 5px 0 0 var(--green-shadow);}
.img-opt.wrong{background:var(--red-bg);border-color:var(--red-border);box-shadow:0 5px 0 0 var(--red-shadow);}
.img-opt:disabled{cursor:default;}

/* ─── Explanation box ─── */
.expl-box{
  background:#fef8d0;border-left:5px solid var(--gold-border);
  padding:10px 12px;font-size:7px;color:var(--text-dim);line-height:1.9;
  display:none;
}
.expl-box.show{display:block;}

/* ─── Pixel inputs ─── */
.px-input{
  font-family:var(--font);font-size:7px;color:var(--text);
  background:#f8edc8;
  border:4px solid var(--border);
  box-shadow:inset 0 3px 0 0 var(--panel-inner);
  padding:8px 10px;width:100%;outline:none;line-height:1.8;
  transition:border-color .1s;
}
.px-input:focus{border-color:var(--green-border);box-shadow:inset 0 3px 0 0 #a0d070;}
.px-input.correct{border-color:var(--green-border);background:#d8f0b0;}
.px-input.wrong{border-color:var(--red-border);background:var(--red-bg);}
.px-inline{
  font-family:var(--font);font-size:7px;color:var(--text);
  background:transparent;border:none;border-bottom:3px solid var(--border);
  padding:2px 6px;min-width:64px;outline:none;text-align:center;display:inline-block;
}
.px-inline:focus{border-bottom-color:var(--green-border);}
.px-inline.correct{border-bottom-color:var(--green-border);color:var(--green);}
.px-inline.wrong{border-bottom-color:var(--red-border);color:var(--red);}
.px-select{
  font-family:var(--font);font-size:7px;color:var(--text);
  background:#f8edc8;border:4px solid var(--border);
  box-shadow:inset 0 3px 0 0 var(--panel-inner);
  padding:8px 10px;width:100%;outline:none;cursor:pointer;
}
.px-select:focus{border-color:var(--green-border);}
.px-select.correct{border-color:var(--green-border);background:#d8f0b0;}
.px-select.wrong{border-color:var(--red-border);background:var(--red-bg);}

/* ─── Matching ─── */
.match-grid{display:grid;grid-template-columns:1fr 1fr;gap:6px;}
.match-chip{
  font-family:var(--font);font-size:6px;color:var(--text);
  background:var(--panel);border:3px solid var(--border);
  box-shadow:0 4px 0 0 var(--shadow);
  padding:8px 6px;cursor:pointer;text-align:center;line-height:1.8;
  position:relative;top:0;
  transition:top .06s,box-shadow .06s,background .06s;
}
.match-chip:hover:not(.matched):not(.correct):not(.wrong-match){background:#f8e8c0;}
.match-chip:active:not(.matched){top:2px;box-shadow:0 2px 0 0 var(--shadow);}
.match-chip.selected{background:#d8f0b0;border-color:var(--green-border);box-shadow:0 4px 0 0 var(--green-shadow);}
.match-chip.matched,.match-chip.correct{background:var(--green-bg);border-color:var(--green-border);box-shadow:0 4px 0 0 var(--green-shadow);color:#1a4010;cursor:default;top:2px;}
.match-chip.wrong-match{background:var(--red-bg);border-color:var(--red-border);box-shadow:0 4px 0 0 var(--red-shadow);color:#601010;cursor:default;}

/* ─── Ordering ─── */
.order-list{display:flex;flex-direction:column;gap:6px;}
.order-item{
  font-family:var(--font);font-size:7px;color:var(--text);
  background:var(--panel);border:3px solid var(--border);
  box-shadow:0 4px 0 0 var(--shadow);
  padding:10px 12px;cursor:grab;
  display:flex;align-items:center;gap:10px;
  user-select:none;line-height:1.8;
  transition:background .06s;
}
.order-item:active{cursor:grabbing;}
.order-item.over{border-color:var(--green-border);background:#f8e8c0;}
.order-item.correct{background:var(--green-bg);border-color:var(--green-border);box-shadow:0 4px 0 0 var(--green-shadow);color:#1a4010;cursor:default;}
.order-item.wrong{background:var(--red-bg);border-color:var(--red-border);box-shadow:0 4px 0 0 var(--red-shadow);color:#601010;cursor:default;}
.order-handle{color:var(--panel-inner);font-size:10px;}

/* ─── Word bank ─── */
.word-bank{display:flex;flex-wrap:wrap;gap:6px;margin-top:10px;}
.word-chip{
  font-family:var(--font);font-size:6px;color:var(--text);
  background:var(--panel-dark);border:3px solid var(--border);
  box-shadow:0 4px 0 0 var(--shadow);
  padding:6px 10px;cursor:grab;user-select:none;
  position:relative;top:0;
  transition:top .06s,box-shadow .06s,background .06s;
}
.word-chip:hover:not(.used){background:#f8e8c0;}
.word-chip:active:not(.used){top:2px;box-shadow:0 2px 0 0 var(--shadow);}
.word-chip.selected-chip{background:var(--green-bg);border-color:var(--green-border);box-shadow:0 4px 0 0 var(--green-shadow);}
.word-chip.used{opacity:.3;pointer-events:none;cursor:default;top:2px;box-shadow:none;}
.drag-blank{
  display:inline-block;min-width:72px;padding:2px 8px;
  font-size:7px;font-family:var(--font);color:var(--text);
  border-bottom:3px solid var(--border);margin:0 4px;
  text-align:center;vertical-align:bottom;cursor:pointer;
}
.drag-blank.filled{border-bottom-color:var(--green-border);color:var(--green);}
.drag-blank.correct{border-bottom-color:var(--green-border);color:var(--green);}
.drag-blank.wrong{border-bottom-color:var(--red-border);color:var(--red);}

/* ─── Mark words ─── */
.word-tokens{display:flex;flex-wrap:wrap;gap:6px;}
.word-token{
  font-family:var(--font);font-size:7px;color:var(--text);
  background:var(--panel);border:3px solid var(--border);
  box-shadow:0 3px 0 0 var(--shadow);
  padding:6px 9px;cursor:pointer;
  position:relative;top:0;
  transition:top .06s,box-shadow .06s,background .06s;
}
.word-token:hover{background:#f8e8c0;}
.word-token.selected{background:var(--blue-bg);border-color:var(--blue-border);box-shadow:0 3px 0 0 var(--blue-shadow);top:2px;}
.word-token.correct{background:var(--green-bg);border-color:var(--green-border);box-shadow:0 3px 0 0 var(--green-shadow);top:2px;cursor:default;}
.word-token.wrong{background:var(--red-bg);border-color:var(--red-border);box-shadow:0 3px 0 0 var(--red-shadow);top:2px;cursor:default;}

/* ─── Word grid (find words) ─── */
.word-grid{display:flex;flex-direction:column;gap:2px;}
.grid-row{display:flex;gap:2px;}
.grid-cell{
  width:26px;height:26px;
  display:flex;align-items:center;justify-content:center;
  font-size:7px;cursor:pointer;
  border:2px solid transparent;
  color:var(--text-dim);
  transition:background .1s;
}
.grid-cell:hover{background:rgba(255,240,190,.5);border-color:var(--border);}
.grid-cell.selecting{background:var(--blue-bg);border-color:var(--blue-border);color:var(--blue);}
.grid-cell.found{background:var(--green-bg);border-color:var(--green-border);color:#1a4010;}
.word-targets{display:flex;flex-wrap:wrap;gap:6px;margin-top:8px;}
.word-target{font-size:7px;padding:3px 8px;border:3px solid var(--border);color:var(--text-dim);font-family:var(--font);background:var(--panel);}
.word-target.found{border-color:var(--green-border);color:var(--green);background:var(--green-bg);text-decoration:line-through;}

/* ─── Table ─── */
.px-table{width:100%;border-collapse:collapse;font-size:6px;}
.px-table th,.px-table td{padding:7px 10px;border:3px solid var(--border);text-align:left;line-height:1.8;}
.px-table th{background:var(--panel-dark);color:var(--text);}
.px-table td{background:var(--panel);color:var(--text-dim);}

/* ─── Bar chart ─── */
.bar-chart{display:flex;flex-direction:column;gap:8px;}
.bar-row{display:flex;align-items:center;gap:8px;}
.bar-lbl{width:80px;font-size:6px;color:var(--text-dim);text-align:right;flex-shrink:0;line-height:1.7;}
.bar-track{flex:1;height:18px;background:var(--panel-dark);border:3px solid var(--border);}
.bar-fill{height:100%;background:var(--green);border-right:2px solid var(--green-border);transition:width .6s;}
.bar-val{width:36px;font-size:6px;color:var(--text);flex-shrink:0;}

/* ─── Accordion ─── */
.accord-item{border:3px solid var(--border);margin-bottom:4px;box-shadow:0 4px 0 0 var(--shadow);}
.accord-hdr{
  width:100%;background:var(--panel-dark);border:none;cursor:pointer;
  padding:9px 12px;font-family:var(--font);font-size:7px;color:var(--text);
  text-align:left;display:flex;justify-content:space-between;align-items:center;line-height:1.8;
  transition:background .1s;
}
.accord-hdr:hover{background:#f0e0b0;}
.accord-body{padding:10px 12px;font-size:7px;color:var(--text-dim);line-height:2;display:none;background:var(--panel);}
.accord-body.open{display:block;}

/* ─── Floating feedback text ─── */
.float-txt{
  position:fixed;left:50%;top:45%;transform:translateX(-50%);
  z-index:500;pointer-events:none;text-align:center;
  font-family:var(--font);
  animation:float-up 1.4s ease-out forwards;
}
@keyframes float-up{
  0%{transform:translateX(-50%) translateY(0);opacity:1;}
  80%{opacity:1;}
  100%{transform:translateX(-50%) translateY(-90px);opacity:0;}
}

/* ─── Result screen (parchment scroll) ─── */
#result{
  position:fixed;inset:0;z-index:200;
  background:rgba(61,40,16,.8);
  display:none;align-items:center;justify-content:center;
  padding:20px;overflow-y:auto;
}
#result.show{display:flex;}
.result-scroll{
  background:var(--panel);
  border:6px solid var(--border);
  box-shadow:0 10px 0 0 var(--shadow),inset 0 0 0 3px var(--panel-inner);
  padding:24px 20px;max-width:380px;width:100%;
  text-align:center;
  display:flex;flex-direction:column;align-items:center;gap:14px;
}
.result-title{font-size:11px;color:var(--text);line-height:1.6;letter-spacing:.06em;}
.result-stars{display:flex;gap:8px;}
.result-star{font-size:22px;color:var(--gold-border);line-height:1;}
.result-star.off{color:rgba(180,140,60,.3);}
.result-stats{width:100%;display:flex;flex-direction:column;gap:8px;border-top:3px solid var(--panel-inner);padding-top:12px;}
.result-row{display:flex;justify-content:space-between;font-size:7px;}
.result-lbl{color:var(--text-dim);}
.result-val{color:var(--text);}
.result-rank{
  font-size:20px;letter-spacing:.1em;color:var(--text);
  background:var(--panel-dark);border:4px solid var(--border);
  box-shadow:0 5px 0 0 var(--shadow);
  padding:8px 20px;display:inline-block;
}

/* ─── Animations ─── */
@keyframes blink{0%,100%{opacity:1;}50%{opacity:0;}}
@keyframes shake{
  0%,100%{transform:translateX(0);}
  20%{transform:translateX(-7px);}40%{transform:translateX(7px);}
  60%{transform:translateX(-5px);}80%{transform:translateX(5px);}
}
.shake{animation:shake .4s ease-out;}
</style>
</head>
<body>

<!-- HUD -->
<div id="hud">
  <span id="hud-title">🌿 SPROUT QUEST</span>
  <div id="hud-hearts"></div>
  <span id="hud-coins">🪙 0</span>
</div>

<!-- Slide area -->
<div id="game"><div id="slide-wrap"></div></div>

<!-- Footer -->
<div id="footer">
  <button class="px-btn px-btn-sec" id="btn-prev" onclick="G.prev()" style="display:none;font-size:7px;padding:8px 14px;">◀ BACK</button>
  <button class="px-btn px-btn-wide" id="btn-next" onclick="G.next()" disabled>CONTINUE ▶</button>
</div>

<!-- Result screen -->
<div id="result">
  <div class="result-scroll">
    <div class="result-title" id="result-title"></div>
    <div class="result-stars" id="result-stars"></div>
    <div class="result-rank" id="result-rank"></div>
    <div class="result-stats" id="result-stats"></div>
    <button class="px-btn px-btn-gold" onclick="G.exit()" style="margin-top:4px;">🌿 FINISH QUEST</button>
  </div>
</div>

<script>
(function(){
'use strict';

// ── State ─────────────────────────────────────────────────────────────────────
var _slides=[], _behaviour={}, _capsule={};
var _current=0, _score=0, _sessionStart=0, _slideStart=0, _answered=false;
var _feedbackCb=null;
var KEYS=['A','B','C','D','E','F','G','H'];
var HEARTS=8;

// ── Bridge ────────────────────────────────────────────────────────────────────
function send(type,payload){
  var msg=payload!==undefined?{type:type,payload:payload}:{type:type};
  window.parent.postMessage(msg,'*');
}

window.addEventListener('message',function(e){
  var d=e.data||{};
  if(d.type==='init'){
    _slides=d.payload.slides||[];
    _behaviour=d.payload.behaviour||{};
    _capsule=d.payload.capsule||{};
    _sessionStart=Date.now();
    _score=0;
    var t=(_capsule.title||'Sprout Quest').slice(0,22);
    document.getElementById('hud-title').textContent='🌿 '+t.toUpperCase();
    updateHUD();
    renderSlide(_slides[0]);
  }
  if(d.type==='feedback'){
    if(_feedbackCb){_feedbackCb(d.payload);_feedbackCb=null;}
    if(d.payload&&d.payload.correct&&d.payload.pointsEarned) _score+=d.payload.pointsEarned;
    updateHUD();
  }
  if(d.type==='slide_show'){
    _current=d.payload.index;
    renderSlide(d.payload.slide);
  }
  if(d.type==='result'){showResult(d.payload);}
});

// ── HUD ───────────────────────────────────────────────────────────────────────
function updateHUD(){
  document.getElementById('hud-coins').textContent='🪙 '+_score;
  var total=_slides.length;
  var hearts=document.getElementById('hud-hearts');
  hearts.innerHTML='';
  var blocks=Math.min(total,HEARTS);
  if(_behaviour.showProgressBar===false){return;}
  var filled=blocks>0?Math.round(((_current)/total)*blocks):0;
  for(var i=0;i<blocks;i++){
    var h=document.createElement('span');
    h.className='hrt '+(i<filled?'hrt-on':'hrt-off');
    h.textContent='♥';
    hearts.appendChild(h);
  }
}

// ── Navigation ────────────────────────────────────────────────────────────────
var G={
  next:function(){
    var next=_current+1;
    if(next>=_slides.length){send('complete',{totalTimeMs:Date.now()-_sessionStart});}
    else{_current=next;renderSlide(_slides[_current]);}
  },
  prev:function(){
    if(_current>0){_current--;renderSlide(_slides[_current]);}
  },
  exit:function(){send('exit');}
};
window.G=G;

function setNextEnabled(on){document.getElementById('btn-next').disabled=!on;}
function setPrevVisible(on){document.getElementById('btn-prev').style.display=on?'':'none';}

// ── Slide render dispatch ─────────────────────────────────────────────────────
function renderSlide(slide){
  if(!slide)return;
  _answered=false;
  _slideStart=Date.now();
  updateHUD();
  setNextEnabled(!slide.isExercise);
  setPrevVisible(_current>0);
  var c=document.getElementById('slide-wrap');
  c.innerHTML='';
  var map={
    video:renderVideo,content:renderContent,text_block:renderTextBlock,
    image_slide:renderImage,accordion:renderAccordion,
    table_slide:renderTable,chart_slide:renderChart,formula_slide:renderFormula,
    single_choice:renderSingleChoice,image_choice:renderImageChoice,
    multiple_choice:renderMultipleChoice,true_false:renderTrueFalse,
    fill_blanks:renderFillBlanks,
    matching:renderMatching,ordering:renderOrdering,
    drag_words:renderDragWords,find_words:renderFindWords,
    mark_words:renderMarkWords,complex_fill_blank:renderComplexFillBlank
  };
  var fn=map[slide.type]||renderContent;
  fn(c,slide);
  document.getElementById('game').scrollTop=0;
}

// ── Utilities ─────────────────────────────────────────────────────────────────
function el(tag,cls,txt){
  var e=document.createElement(tag);
  if(cls)e.className=cls;
  if(txt!=null)e.textContent=txt;
  return e;
}
function typeTag(label,variant){
  var d=el('div','slide-type',('🌿 '+label).toUpperCase());
  return d;
}
function expl(){return el('div','expl-box','');}
function showExpl(box,txt){if(!txt)return;box.textContent='💡 '+txt;box.classList.add('show');}

function shuffle(arr){
  var a=arr.slice();
  for(var i=a.length-1;i>0;i--){var j=Math.floor(Math.random()*(i+1));var t=a[i];a[i]=a[j];a[j]=t;}
  return a;
}
function maybeShuffled(arr){return _behaviour.shuffleOptions?shuffle(arr):arr.slice();}

function submitAnswer(slideId,value,cb){
  send('answer',{slideId:slideId,value:value,timeSpentMs:Date.now()-_slideStart});
  if(_behaviour.instantFeedback!==false){_feedbackCb=cb;}
  else{setNextEnabled(true);if(cb)cb({correct:null,pointsEarned:0});}
}

function flashFeedback(correct,pts){
  var div=document.createElement('div');
  div.className='float-txt';
  if(correct){
    var phrases=['✓ CORRECT!','✓ NICE!','✓ PERFECT!','✓ GREAT!'];
    div.innerHTML=
      '<div style="font-size:13px;color:#3d7018;text-shadow:2px 2px 0 #2a5010;">'+
        phrases[Math.floor(Math.random()*phrases.length)]+
      '</div>'+
      (pts?'<div style="font-size:9px;color:'+('var(--gold)')+ ';margin-top:6px;text-shadow:1px 1px 0 #705000;">+'+pts+' 🪙</div>':'');
  }else if(correct===false){
    var bads=['✗ OOPS!','✗ MISS!','✗ WRONG!','✗ TRY AGAIN!'];
    div.innerHTML='<div style="font-size:13px;color:#802020;text-shadow:2px 2px 0 #601010;">'+bads[Math.floor(Math.random()*bads.length)]+'</div>';
    var sw=document.getElementById('slide-wrap');
    sw.classList.add('shake');
    setTimeout(function(){sw.classList.remove('shake');},400);
  }
  document.body.appendChild(div);
  setTimeout(function(){if(div.parentNode)div.parentNode.removeChild(div);},1400);
}

// ── Renderers ─────────────────────────────────────────────────────────────────

function renderVideo(c,slide){
  c.appendChild(typeTag('VIDEO'));
  var box=el('div','px-panel');
  if(slide.videoUri){
    var vid=document.createElement('video');
    vid.controls=true;
    vid.style.cssText='width:100%;max-height:260px;display:block;image-rendering:pixelated;border:4px solid var(--border);background:#000;';
    vid.src=slide.videoUri;
    if(slide.videoThumbnailUri)vid.poster=slide.videoThumbnailUri;
    box.appendChild(vid);
  }else{
    box.style.cssText='text-align:center;padding:30px;font-size:28px;';
    box.textContent='🎬';
  }
  c.appendChild(box);
  setNextEnabled(true);
}

function renderContent(c,slide){
  var box=el('div','px-panel');
  if(slide.contentTitle){var t=el('div','slide-title',slide.contentTitle);c.appendChild(t);}
  if(slide.imageUri){
    var img=document.createElement('img');
    img.src=slide.imageUri;img.className='slide-img';img.style.marginBottom='10px';
    box.appendChild(img);
  }
  if(slide.contentBody)box.appendChild(el('div','slide-body',slide.contentBody));
  c.appendChild(box);
}

function renderTextBlock(c,slide){
  var box=el('div','px-panel');
  if(slide.textTitle)box.appendChild(el('div','slide-title',slide.textTitle));
  if(slide.textBody){var p=el('div','slide-body');p.innerHTML=slide.textBody;box.appendChild(p);}
  c.appendChild(box);
}

function renderImage(c,slide){
  c.appendChild(typeTag('IMAGE'));
  if(slide.imageUri){var img=document.createElement('img');img.src=slide.imageUri;img.className='slide-img';c.appendChild(img);}
  if(slide.imageCaption)c.appendChild(el('div','slide-body',slide.imageCaption));
}

function renderFormula(c,slide){
  c.appendChild(typeTag('FORMULA'));
  var box=el('div','px-panel-blue');
  box.style.cssText='text-align:center;font-size:13px;color:var(--blue);padding:20px;';
  box.textContent=slide.latex||'';
  c.appendChild(box);
}

function renderAccordion(c,slide){
  c.appendChild(typeTag('EXPAND'));
  (slide.accordionItems||[]).forEach(function(item){
    var wrap=el('div','accord-item');
    var body=el('div','accord-body',item.body);
    var arr=el('span',null,'▼');
    var hdr=el('button','accord-hdr');
    hdr.appendChild(el('span',null,item.title));
    hdr.appendChild(arr);
    hdr.onclick=function(){var o=body.classList.contains('open');body.classList.toggle('open',!o);arr.textContent=o?'▼':'▲';};
    wrap.appendChild(hdr);wrap.appendChild(body);c.appendChild(wrap);
  });
}

function renderTable(c,slide){
  c.appendChild(typeTag('DATA TABLE'));
  var rows=slide.tableRows||[];if(!rows.length)return;
  var table=el('table','px-table');
  rows.forEach(function(row,ri){
    var tr=el('tr');
    row.forEach(function(cell){tr.appendChild(el(ri===0?'th':'td',null,cell));});
    table.appendChild(tr);
  });
  var box=el('div','px-panel');box.style.overflowX='auto';box.appendChild(table);c.appendChild(box);
}

function renderChart(c,slide){
  c.appendChild(typeTag('CHART'));
  var data=slide.chartData||[];
  var max=0;data.forEach(function(d){if(d.value>max)max=d.value;});if(!max)max=1;
  var chart=el('div','bar-chart');
  data.forEach(function(d){
    var row=el('div','bar-row');
    var lbl=el('div','bar-lbl',d.label);
    var track=el('div','bar-track');
    var fill=el('div','bar-fill');fill.style.width=((d.value/max)*100)+'%';
    var val=el('div','bar-val',String(d.value));
    track.appendChild(fill);row.appendChild(lbl);row.appendChild(track);row.appendChild(val);
    chart.appendChild(row);
  });
  var box=el('div','px-panel');box.appendChild(chart);c.appendChild(box);
}

// ─ Exercise: single choice ───────────────────────────────────────────────────
function renderSingleChoice(c,slide){
  c.appendChild(typeTag('QUEST'));
  var dialog=el('div','dialog');
  dialog.appendChild(el('div','dialog-text',slide.question||''));
  dialog.appendChild(el('span','dialog-arrow','▼'));
  c.appendChild(dialog);

  var opts=maybeShuffled(slide.options||[]);
  var grid=el('div','options');
  var explBox=expl();

  opts.forEach(function(opt,i){
    var btn=el('button','opt-btn');
    btn.appendChild(el('span','opt-key',KEYS[i]));
    btn.appendChild(el('span',null,opt.text));
    btn.onclick=function(){
      if(_answered)return;
      _answered=true;
      btn.classList.add('selected');
      submitAnswer(slide.id,opt.id,function(fb){
        opts.forEach(function(o,j){
          var b=grid.children[j];
          b.disabled=true;
          if(o.isCorrect)b.classList.add('correct');
          else if(o.id===opt.id&&!o.isCorrect)b.classList.add('wrong');
        });
        flashFeedback(fb.correct,fb.pointsEarned);
        showExpl(explBox,slide.explanation);
        setNextEnabled(true);
      });
    };
    grid.appendChild(btn);
  });
  c.appendChild(grid);
  c.appendChild(explBox);
}

// ─ Exercise: image choice ────────────────────────────────────────────────────
function renderImageChoice(c,slide){
  c.appendChild(typeTag('IMAGE QUEST'));
  var dialog=el('div','dialog');
  dialog.appendChild(el('div','dialog-text',slide.question||''));
  dialog.appendChild(el('span','dialog-arrow','▼'));
  c.appendChild(dialog);

  var opts=maybeShuffled(slide.options||[]);
  var grid=el('div','img-opts');
  var explBox=expl();

  opts.forEach(function(opt,i){
    var btn=document.createElement('button');
    btn.className='img-opt';
    if(opt.imageUri){var img=document.createElement('img');img.src=opt.imageUri;btn.appendChild(img);}
    btn.appendChild(el('span',null,opt.text));
    btn.onclick=function(){
      if(_answered)return;
      _answered=true;
      submitAnswer(slide.id,opt.id,function(fb){
        for(var j=0;j<grid.children.length;j++){
          grid.children[j].disabled=true;
          if(opts[j].isCorrect)grid.children[j].classList.add('correct');
          else if(opts[j].id===opt.id)grid.children[j].classList.add('wrong');
        }
        flashFeedback(fb.correct,fb.pointsEarned);
        showExpl(explBox,slide.explanation);
        setNextEnabled(true);
      });
    };
    grid.appendChild(btn);
  });
  c.appendChild(grid);
  c.appendChild(explBox);
}

// ─ Exercise: multiple choice ─────────────────────────────────────────────────
function renderMultipleChoice(c,slide){
  c.appendChild(typeTag('MULTI QUEST'));
  var dialog=el('div','dialog');
  dialog.appendChild(el('div','dialog-text',slide.question||''));
  dialog.appendChild(el('span','dialog-arrow','▼'));
  c.appendChild(dialog);

  var opts=maybeShuffled(slide.options||[]);
  var selected={};
  var grid=el('div','options');
  var explBox=expl();

  opts.forEach(function(opt,i){
    var btn=el('button','opt-btn');
    var keyEl=el('span','opt-key','☐');
    btn.appendChild(keyEl);
    btn.appendChild(el('span',null,opt.text));
    btn.onclick=function(){
      if(_answered)return;
      if(selected[opt.id]){delete selected[opt.id];keyEl.textContent='☐';btn.classList.remove('selected');}
      else{selected[opt.id]=true;keyEl.textContent='☑';btn.classList.add('selected');}
      checkBtn.disabled=Object.keys(selected).length===0;
    };
    grid.appendChild(btn);
  });

  var checkBtn=el('button','px-btn px-btn-wide','CHECK ▶');
  checkBtn.disabled=true;
  checkBtn.onclick=function(){
    if(_answered)return;
    _answered=true;checkBtn.disabled=true;
    submitAnswer(slide.id,Object.keys(selected),function(fb){
      opts.forEach(function(o,j){
        var b=grid.children[j];b.disabled=true;
        if(o.isCorrect)b.classList.add('correct');
        else if(selected[o.id])b.classList.add('wrong');
      });
      flashFeedback(fb.correct,fb.pointsEarned);
      showExpl(explBox,slide.explanation);
      setNextEnabled(true);
    });
  };
  c.appendChild(grid);
  c.appendChild(checkBtn);
  c.appendChild(explBox);
}

// ─ Exercise: true / false ────────────────────────────────────────────────────
function renderTrueFalse(c,slide){
  c.appendChild(typeTag('TRUE OR FALSE'));
  var dialog=el('div','dialog');
  dialog.appendChild(el('div','dialog-text',slide.question||''));
  dialog.appendChild(el('span','dialog-arrow','▼'));
  c.appendChild(dialog);
  var explBox=expl();
  var row=el('div','options');

  ['true','false'].forEach(function(val){
    var btn=el('button','opt-btn');
    btn.appendChild(el('span','opt-key',val==='true'?'T':'F'));
    btn.appendChild(el('span',null,val==='true'?'✓  TRUE':'✗  FALSE'));
    btn.onclick=function(){
      if(_answered)return;
      _answered=true;
      row.querySelectorAll('.opt-btn').forEach(function(b){b.disabled=true;});
      submitAnswer(slide.id,val,function(fb){
        row.querySelectorAll('.opt-btn').forEach(function(b){
          var bv=b.textContent.indexOf('TRUE')>=0?'true':'false';
          if(bv===String(slide.correctAnswer))b.classList.add('correct');
          else if(bv===val&&val!==String(slide.correctAnswer))b.classList.add('wrong');
        });
        flashFeedback(fb.correct,fb.pointsEarned);
        showExpl(explBox,slide.explanation);
        setNextEnabled(true);
      });
    };
    row.appendChild(btn);
  });
  c.appendChild(row);
  c.appendChild(explBox);
}

// ─ Exercise: fill blank ──────────────────────────────────────────────────────
function renderFillBlank(c,slide){
  c.appendChild(typeTag('FILL IN'));
  if(slide.question){var d=el('div','dialog');d.appendChild(el('div','dialog-text',slide.question));d.appendChild(el('span','dialog-arrow','▼'));c.appendChild(d);}
  var blanks=slide.blanks||[];
  var inputs=[];
  var explBox=expl();

  blanks.forEach(function(blank){
    var row=el('div',null);
    row.style.cssText='margin-bottom:8px;font-size:7px;line-height:2.8;';
    if(blank.before)row.appendChild(document.createTextNode(blank.before+' '));
    var inp=el('input','px-inline');inp.type='text';inp.placeholder='___';
    inputs.push({inp:inp,blank:blank});
    row.appendChild(inp);
    if(blank.after)row.appendChild(document.createTextNode(' '+blank.after));
    c.appendChild(row);
  });

  var btn=el('button','px-btn px-btn-wide','SUBMIT ▶');
  btn.onclick=function(){
    if(_answered)return;
    _answered=true;btn.disabled=true;
    var vals=inputs.map(function(x){return x.inp.value.trim();});
    submitAnswer(slide.id,vals,function(fb){
      var corr=Array.isArray(fb.correctValue)?fb.correctValue:[];
      inputs.forEach(function(x,i){
        x.inp.disabled=true;
        var ok=x.inp.value.trim().toLowerCase()===(corr[i]||x.blank.answer||'').toLowerCase();
        if(!ok&&x.blank.alternatives)x.blank.alternatives.forEach(function(a){if(x.inp.value.trim().toLowerCase()===a.toLowerCase())ok=true;});
        x.inp.classList.add(ok?'correct':'wrong');
      });
      flashFeedback(fb.correct,fb.pointsEarned);
      showExpl(explBox,slide.explanation);
      setNextEnabled(true);
    });
  };
  c.appendChild(btn);
  c.appendChild(explBox);
}

// ─ Exercise: fill_blanks (inline) ────────────────────────────────────────────
function renderFillBlanks(c,slide){
  c.appendChild(typeTag('FILL IN'));
  if(slide.question){var d=el('div','dialog');d.appendChild(el('div','dialog-text',slide.question));d.appendChild(el('span','dialog-arrow','▼'));c.appendChild(d);}
  var answers=slide.fillBlanksAnswers||[];
  var inputs=[];
  var idx=0;
  var para=el('p',null);para.style.cssText='font-size:7px;line-height:3.2;';
  var parts=(slide.fillBlanksParagraph||'').split('___');
  parts.forEach(function(part,i){
    para.appendChild(document.createTextNode(part));
    if(i<parts.length-1&&idx<answers.length){
      var inp=el('input','px-inline');inp.type='text';inp.placeholder='___';
      inputs.push({inp:inp,answer:answers[idx++]});
      para.appendChild(inp);
    }
  });
  c.appendChild(para);
  var explBox=expl();
  var btn=el('button','px-btn px-btn-wide','SUBMIT ▶');
  btn.onclick=function(){
    if(_answered)return;
    _answered=true;btn.disabled=true;
    submitAnswer(slide.id,inputs.map(function(x){return x.inp.value.trim();}),function(fb){
      inputs.forEach(function(x){
        x.inp.disabled=true;
        var ok=x.inp.value.trim().toLowerCase()===x.answer.answer.toLowerCase();
        if(!ok&&x.answer.alternatives)x.answer.alternatives.forEach(function(a){if(x.inp.value.trim().toLowerCase()===a.toLowerCase())ok=true;});
        x.inp.classList.add(ok?'correct':'wrong');
      });
      flashFeedback(fb.correct,fb.pointsEarned);
      showExpl(explBox,slide.explanation);
      setNextEnabled(true);
    });
  };
  c.appendChild(btn);
  c.appendChild(explBox);
}

// ─ Exercise: matching ────────────────────────────────────────────────────────
function renderMatching(c,slide){
  c.appendChild(typeTag('MATCH'));
  if(slide.question){var d=el('div','dialog');d.appendChild(el('div','dialog-text',slide.question));d.appendChild(el('span','dialog-arrow','▼'));c.appendChild(d);}
  var pairs=slide.pairs||[];
  var rightSh=shuffle(pairs.map(function(p){return{id:p.id,text:p.right};}));
  var matchMap={};var selLeft=null,selRight=null;
  var leftEls={},rightEls={};
  var explBox=expl();
  var grid=el('div','match-grid');

  pairs.forEach(function(p){
    var lc=el('div','match-chip',p.left);
    lc.onclick=function(){
      if(lc.classList.contains('matched')||lc.classList.contains('correct'))return;
      if(selLeft)selLeft.classList.remove('selected');
      selLeft=lc;lc.classList.add('selected');tryMatch();
    };
    leftEls[p.id]=lc;grid.appendChild(lc);
  });
  rightSh.forEach(function(r){
    var rc=el('div','match-chip',r.text);
    rc.onclick=function(){
      if(rc.classList.contains('matched')||rc.classList.contains('correct'))return;
      if(selRight)selRight.classList.remove('selected');
      selRight=rc;rc.classList.add('selected');tryMatch();
    };
    rightEls[r.id]=rc;grid.appendChild(rc);
  });

  function tryMatch(){
    if(!selLeft||!selRight)return;
    var lid=null;
    Object.keys(leftEls).forEach(function(k){if(leftEls[k]===selLeft)lid=k;});
    matchMap[lid]=selRight.textContent;
    selLeft.classList.remove('selected');selLeft.classList.add('matched');
    selRight.classList.remove('selected');selRight.classList.add('matched');
    selLeft=null;selRight=null;
    checkBtn.disabled=Object.keys(matchMap).length<pairs.length;
  }

  var checkBtn=el('button','px-btn px-btn-wide','CHECK ▶');
  checkBtn.disabled=true;
  checkBtn.onclick=function(){
    if(_answered)return;_answered=true;checkBtn.disabled=true;
    submitAnswer(slide.id,matchMap,function(fb){
      pairs.forEach(function(p){
        var ok=matchMap[p.id]===p.right;
        leftEls[p.id].classList.remove('matched');
        leftEls[p.id].classList.add(ok?'correct':'wrong-match');
      });
      flashFeedback(fb.correct,fb.pointsEarned);
      showExpl(explBox,slide.explanation);setNextEnabled(true);
    });
  };
  c.appendChild(grid);c.appendChild(checkBtn);c.appendChild(explBox);
}

// ─ Exercise: ordering ────────────────────────────────────────────────────────
function renderOrdering(c,slide){
  c.appendChild(typeTag('SORT'));
  if(slide.question){var d=el('div','dialog');d.appendChild(el('div','dialog-text',slide.question));d.appendChild(el('span','dialog-arrow','▼'));c.appendChild(d);}
  var items=shuffle(slide.orderItems||[]);
  var dragSrc=null;
  var list=el('div','order-list');
  var explBox=expl();

  function buildList(){
    list.innerHTML='';
    items.forEach(function(item,i){
      var row=el('div','order-item');
      row.draggable=true;
      row.appendChild(el('span','order-handle','⣿'));
      row.appendChild(document.createTextNode(item.label));
      row.addEventListener('dragstart',function(){dragSrc=i;row.style.opacity='.5';});
      row.addEventListener('dragend',function(){row.style.opacity='';});
      row.addEventListener('dragover',function(e){e.preventDefault();row.classList.add('over');});
      row.addEventListener('dragleave',function(){row.classList.remove('over');});
      row.addEventListener('drop',function(){
        row.classList.remove('over');
        if(dragSrc===null||dragSrc===i)return;
        var moved=items.splice(dragSrc,1)[0];items.splice(i,0,moved);dragSrc=null;buildList();
      });
      list.appendChild(row);
    });
  }
  buildList();

  var checkBtn=el('button','px-btn px-btn-wide','LOCK ORDER ▶');
  checkBtn.onclick=function(){
    if(_answered)return;_answered=true;checkBtn.disabled=true;
    submitAnswer(slide.id,items.map(function(x){return x.id;}),function(fb){
      var correct=slide.correctOrder||[];
      list.querySelectorAll('.order-item').forEach(function(row,i){
        row.draggable=false;row.classList.add(items[i].id===correct[i]?'correct':'wrong');
      });
      flashFeedback(fb.correct,fb.pointsEarned);showExpl(explBox,slide.explanation);setNextEnabled(true);
    });
  };
  c.appendChild(list);c.appendChild(checkBtn);c.appendChild(explBox);
}

// ─ Exercise: drag words ──────────────────────────────────────────────────────
function renderDragWords(c,slide){
  c.appendChild(typeTag('DRAG WORDS'));
  if(slide.question){var d=el('div','dialog');d.appendChild(el('div','dialog-text',slide.question));d.appendChild(el('span','dialog-arrow','▼'));c.appendChild(d);}
  var template=slide.dragWordsTemplate||'';
  var correctWords=[],blanks=[];
  var blankIdx=0;
  var htmlStr=template.replace(/\[([^\]]+)\]/g,function(_,word){
    blankIdx++;
    var id='dwb'+blankIdx;
    correctWords.push(word);blanks.push({id:id,word:word});
    return '<span id="'+id+'" class="drag-blank" onclick="window._dwClick(\''+id+'\')" ondragover="event.preventDefault()" ondrop="window._dwDrop(event,\''+id+'\')"> &nbsp; </span>';
  });
  var sentEl=el('p',null);sentEl.style.cssText='font-size:7px;line-height:3.5;font-family:var(--font);color:var(--text);';sentEl.innerHTML=htmlStr;
  c.appendChild(sentEl);

  var allWords=shuffle(correctWords.concat(slide.dragWordsBank||[]));
  var bank=el('div','word-bank');
  var filledMap={};var lastChip=null;

  allWords.forEach(function(word){
    var chip=el('div','word-chip',word);
    chip.draggable=true;chip.dataset.word=word;
    chip.addEventListener('dragstart',function(e){e.dataTransfer.setData('text/plain',word);lastChip=chip;});
    chip.addEventListener('click',function(){
      if(chip.classList.contains('used'))return;
      if(lastChip&&lastChip!==chip)lastChip.classList.remove('selected-chip');
      lastChip=chip;chip.classList.toggle('selected-chip');
    });
    bank.appendChild(chip);
  });
  c.appendChild(bank);

  window._dwDrop=function(e,bid){
    var word=e.dataTransfer.getData('text/plain')||(lastChip&&lastChip.dataset.word)||'';
    if(word)fillBW(bid,word);
  };
  window._dwClick=function(bid){
    var word=lastChip&&!lastChip.classList.contains('used')?lastChip.dataset.word:null;
    if(word){fillBW(bid,word);if(lastChip){lastChip.classList.remove('selected-chip');lastChip=null;}}
  };
  function fillBW(bid,word){
    filledMap[bid]=word;
    var be=document.getElementById(bid);
    if(be){be.textContent=word;be.classList.add('filled');}
    var chips=bank.querySelectorAll('.word-chip');
    for(var i=0;i<chips.length;i++){
      if(chips[i].dataset.word===word&&!chips[i].classList.contains('used')){
        chips[i].classList.add('used');break;
      }
    }
  }

  var explBox=expl();
  var btn=el('button','px-btn px-btn-wide','CHECK ▶');
  btn.onclick=function(){
    if(_answered)return;_answered=true;btn.disabled=true;
    submitAnswer(slide.id,filledMap,function(fb){
      blanks.forEach(function(b){
        var be=document.getElementById(b.id);
        if(be)be.classList.add(filledMap[b.id]===b.word?'correct':'wrong');
      });
      flashFeedback(fb.correct,fb.pointsEarned);showExpl(explBox,slide.explanation);setNextEnabled(true);
    });
  };
  c.appendChild(btn);c.appendChild(explBox);
}

// ─ Exercise: find words ──────────────────────────────────────────────────────
function renderFindWords(c,slide){
  c.appendChild(typeTag('WORD HUNT'));
  if(slide.question){var d=el('div','dialog');d.appendChild(el('div','dialog-text',slide.question));d.appendChild(el('span','dialog-arrow','▼'));c.appendChild(d);}
  var grid=slide.findWordsGrid||[];
  var targets=slide.findWordsTargets||[];
  var found=[];var selecting=false,selStart=null,selCells=[];
  var explBox=expl();
  var box=el('div','px-panel');
  var gridEl=el('div','word-grid');

  grid.forEach(function(row,ri){
    var rowEl=el('div','grid-row');
    row.forEach(function(letter,ci){
      var cell=el('div','grid-cell',letter);
      cell.dataset.r=ri;cell.dataset.c=ci;
      cell.addEventListener('mousedown',function(){
        selecting=true;selStart={ri:ri,ci:ci};
        selCells=[cell];cell.classList.add('selecting');
      });
      cell.addEventListener('mouseover',function(){
        if(!selecting)return;
        selCells.forEach(function(sc){sc.classList.remove('selecting');});selCells=[];
        var dr=Math.sign(ri-selStart.ri),dc=Math.sign(ci-selStart.ci);
        var r=selStart.ri,cc=selStart.ci;
        while(r>=0&&r<grid.length&&cc>=0&&cc<row.length){
          var ce=gridEl.querySelector('[data-r="'+r+'"][data-c="'+cc+'"]');
          if(ce){ce.classList.add('selecting');selCells.push(ce);}
          if(r===ri&&cc===ci)break;r+=dr;cc+=dc;
        }
      });
      rowEl.appendChild(cell);
    });
    gridEl.appendChild(rowEl);
  });

  document.addEventListener('mouseup',function onUp(){
    if(!selecting)return;selecting=false;
    var word=selCells.map(function(sc){return sc.textContent;}).join('');
    if(targets.indexOf(word)>=0&&found.indexOf(word)<0){
      found.push(word);
      selCells.forEach(function(sc){sc.classList.remove('selecting');sc.classList.add('found');});
      var tEl=box.querySelector('[data-wt="'+word+'"]');if(tEl)tEl.classList.add('found');
    }else{selCells.forEach(function(sc){sc.classList.remove('selecting');});}
    selCells=[];
    if(found.length===targets.length){
      _answered=true;
      submitAnswer(slide.id,found.slice(),function(fb){flashFeedback(fb.correct,fb.pointsEarned);showExpl(explBox,slide.explanation);setNextEnabled(true);});
    }
  });

  box.appendChild(gridEl);
  var wt=el('div','word-targets');
  targets.forEach(function(w){var s=el('span','word-target',w);s.dataset.wt=w;wt.appendChild(s);});
  box.appendChild(wt);
  c.appendChild(box);c.appendChild(explBox);
}

// ─ Exercise: mark words ──────────────────────────────────────────────────────
function renderMarkWords(c,slide){
  c.appendChild(typeTag('MARK WORDS'));
  if(slide.question){var d=el('div','dialog');d.appendChild(el('div','dialog-text',slide.question));d.appendChild(el('span','dialog-arrow','▼'));c.appendChild(d);}
  var words=(slide.markWordsText||'').split(/\s+/).filter(Boolean);
  var selected={};
  var explBox=expl();
  var tokens=el('div','word-tokens');
  words.forEach(function(word){
    var tok=el('span','word-token',word);
    tok.onclick=function(){
      if(_answered)return;
      if(selected[word]){delete selected[word];tok.classList.remove('selected');}
      else{selected[word]=true;tok.classList.add('selected');}
    };
    tokens.appendChild(tok);
  });
  var btn=el('button','px-btn px-btn-wide','MARK & CHECK ▶');
  btn.onclick=function(){
    if(_answered)return;_answered=true;btn.disabled=true;
    submitAnswer(slide.id,Object.keys(selected),function(fb){
      var correct=slide.markWordsCorrect||[];
      tokens.querySelectorAll('.word-token').forEach(function(tok){
        var w=tok.textContent;tok.classList.remove('selected');
        if(correct.indexOf(w)>=0)tok.classList.add('correct');
        else if(selected[w])tok.classList.add('wrong');
      });
      flashFeedback(fb.correct,fb.pointsEarned);showExpl(explBox,slide.explanation);setNextEnabled(true);
    });
  };
  c.appendChild(tokens);c.appendChild(btn);c.appendChild(explBox);
}

// ─ Exercise: complex fill blank ──────────────────────────────────────────────
function renderComplexFillBlank(c,slide){
  c.appendChild(typeTag('CHALLENGE'));
  if(slide.question){var d=el('div','dialog');d.appendChild(el('div','dialog-text',slide.question));d.appendChild(el('span','dialog-arrow','▼'));c.appendChild(d);}
  var blanks=slide.complexBlanks||[];var selects=[];var explBox=expl();
  blanks.forEach(function(blank){
    var row=el('div',null);row.style.marginBottom='10px';
    if(blank.sentence)row.appendChild(el('div','slide-body',blank.sentence));
    var sel=document.createElement('select');sel.className='px-select';
    var def=document.createElement('option');def.value='';def.textContent='-- SELECT --';sel.appendChild(def);
    shuffle(blank.options||[]).forEach(function(opt){var o=document.createElement('option');o.value=opt;o.textContent=opt;sel.appendChild(o);});
    selects.push({sel:sel,blank:blank});row.appendChild(sel);c.appendChild(row);
  });
  var btn=el('button','px-btn px-btn-wide','CONFIRM ▶');
  btn.onclick=function(){
    if(_answered)return;_answered=true;btn.disabled=true;
    var vals=selects.map(function(x){return x.sel.value;});
    submitAnswer(slide.id,vals,function(fb){
      selects.forEach(function(x,i){
        x.sel.disabled=true;
        var ok=vals[i].toLowerCase()===x.blank.answer.toLowerCase();
        if(!ok&&x.blank.alternatives)x.blank.alternatives.forEach(function(a){if(vals[i].toLowerCase()===a.toLowerCase())ok=true;});
        x.sel.classList.add(ok?'correct':'wrong');
      });
      flashFeedback(fb.correct,fb.pointsEarned);showExpl(explBox,slide.explanation);setNextEnabled(true);
    });
  };
  c.appendChild(btn);c.appendChild(explBox);
}

// ── Result screen ─────────────────────────────────────────────────────────────
function showResult(result){
  document.getElementById('game').style.display='none';
  document.getElementById('footer').style.display='none';
  var rs=document.getElementById('result');rs.classList.add('show');

  var pct=result.percentage||0;
  var victory=pct>=50;
  var title=document.getElementById('result-title');

  if(victory){
    title.textContent= pct>=90?'★ QUEST COMPLETE! ★':pct>=70?'WELL DONE! 🌿':'MISSION CLEAR!';
  }else{
    title.textContent='... GAME OVER ...';
  }

  // Stars
  var starsDiv=document.getElementById('result-stars');starsDiv.innerHTML='';
  var sc=pct>=90?3:pct>=70?2:pct>=50?1:0;
  for(var i=0;i<3;i++){
    starsDiv.appendChild(el('span','result-star'+(i<sc?'':' off'),'★'));
  }

  // Rank
  var rankEl=document.getElementById('result-rank');
  rankEl.textContent='RANK  '+( pct>=90?'S':pct>=80?'A':pct>=70?'B':pct>=60?'C':pct>=50?'D':'F');

  // Stats
  var stats=document.getElementById('result-stats');stats.innerHTML='';
  function addRow(lbl,val){
    var row=el('div','result-row');
    row.appendChild(el('span','result-lbl',lbl));
    row.appendChild(el('span','result-val',val));
    stats.appendChild(row);
  }
  addRow('COINS EARNED',_score+' 🪙');
  addRow('ACCURACY',pct+'%');
  addRow('CORRECT',(result.correctCount||0)+' / '+(result.totalExerciseCount||0));
  addRow('TIME',Math.round((result.totalTimeMs||0)/1000)+'s');
}

// ── Boot ──────────────────────────────────────────────────────────────────────
window.parent.postMessage({type:'ready'},'*');

})();
</script>
</body>
</html>`
